import React from 'react'
import ModalActions from 'reducers/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { SidebarNavItem, MaterialIcon } from '@dsny/dsny-component-library'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import styles from '../Dropdowns.module.scss'

const ReleasePlaylistDropdown = ({
  renderTrigger,
  triggerAsChild = false
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const { currentReleaseInformation: release } = useSelector(
    (state) => state.releases
  )

  const getTrackIds = () => {
    if (release) {
      return release.songs
        .filter((s) => s.assettype === 'audio')
        .map((item) => item.trackid)
    }

    return []
  }

  const getTrackNames = () => {
    if (release) {
      return `${release.artist} - ${release.title}`
    }

    return ''
  }

  const createNewPlaylist = () => {
    dispatch(PlaylistActions.setSongsToAdd(getTrackIds()))
    dispatch(ModalActions.openPlaylistModal('release_playlist_dropdown'))

    trackEvent('Release Add To Playlist', {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      playlist_id: '',
      playlist_name: 'new_playlist',
      track_count: normalizeValue(getTrackIds().length),
      source: 'release_information',
      type: 'playlist'
    })
  }

  const handleAddToPlaylist = async (playlist) => {
    try {
      const onSuccess = () => {
        const notificationMessage = {
          songTitle: getTrackNames(),
          playlistTitle: playlist.name
        }

        dispatch(
          NotificationActions.broadcastMessage(
            notificationMessage,
            NotificationType.addedToPlaylist
          )
        )
      }

      trackEvent('Release Add To Playlist', {
        release_id: normalizeValue(release?.releaseid),
        artist: normalizeValue(release?.artist),
        title: normalizeValue(release?.title),
        playlist_id: normalizeValue(playlist?.playlistid),
        playlist_name: normalizeValue(playlist?.name),
        track_count: normalizeValue(getTrackIds().length),
        source: 'release_information',
        type: 'playlist'
      })

      dispatch(
        PlaylistThunks.addTracks(playlist.playlistid, getTrackIds(), onSuccess)
      )
    } catch (error) {
      console.warn(error)
    }
  }

  return (
    <DropdownMenu.Root>
      {renderTrigger ? (
        <DropdownMenu.Trigger asChild={triggerAsChild}>
          {renderTrigger()}
        </DropdownMenu.Trigger>
      ) : (
        <DropdownMenu.Trigger>
          <SidebarNavItem
            as="button"
            label={t('general.addto')}
            iconName="AddSong"
            setting="player"
            mode="mobile"
            isBackgroundTransparent="true"
          />
        </DropdownMenu.Trigger>
      )}

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100
          }}
          className={styles.DropdownMenuContent}
          sideOffset={5}
          avoidCollisions
        >
          {playlistAbstracts?.map((playlist) => {
            return (
              <DropdownMenu.Item
                key={playlist?.playlistid}
                className={styles.DropdownMenuItem}
                onClick={() => handleAddToPlaylist(playlist)}
              >
                {playlist?.name}
              </DropdownMenu.Item>
            )
          })}

          <DropdownMenu.Item
            className={styles.DropdownMenuItem}
            onClick={createNewPlaylist}
          >
            New playlist{' '}
            <div className={styles.RightSlot}>
              <MaterialIcon name="Add" />
            </div>
          </DropdownMenu.Item>

          <DropdownMenu.Arrow
            className={styles.DropdownMenuArrow}
            style={{ fill: theme.colors.neutralW20 }}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default ReleasePlaylistDropdown
